import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import LoaderProgressComponent from '../../../Components/menu_planner/LoaderProgressComponent';
import { queryKeys } from '../../../utils/queryKeys';
import DeleteIngredientDialog from './DeleteIngredientDialog';
import {
  createIngredient,
  deleteIngredient,
  getAllIngredients,
  updateIngredient,
} from './IngredientManagementApi';
import IngredientManagementDialog from './IngredientManagementDialog';

const getIngredientErrorMessage = (error, fallbackMessage) => {
  return error?.response?.data?.message ?? fallbackMessage;
};

const sortIngredients = (ingredients = []) => {
  return [...ingredients].sort((firstIngredient, secondIngredient) => {
    const englishComparison = (firstIngredient?.nameEn ?? '').localeCompare(
      secondIngredient?.nameEn ?? ''
    );

    if (englishComparison !== 0) {
      return englishComparison;
    }

    return (firstIngredient?.nameNo ?? '').localeCompare(
      secondIngredient?.nameNo ?? ''
    );
  });
};

const IngredientManagementScreen = () => {
  const queryClient = useQueryClient();
  const [isIngredientDialogOpen, setIsIngredientDialogOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [ingredientToDelete, setIngredientToDelete] = useState(null);

  const { data, isLoading, isError } = useQuery(
    queryKeys.getAllIngredientsData,
    getAllIngredients,
    {
      refetchOnWindowFocus: false,
    }
  );

  const orderedIngredients = useMemo(
    () => sortIngredients(data?.data ?? []),
    [data?.data]
  );

  const invalidateIngredients = () => {
    return queryClient.invalidateQueries(queryKeys.getAllIngredientsData);
  };

  const createIngredientMutation = useMutation(createIngredient, {
    onSuccess: async () => {
      await invalidateIngredients();
      toast.success('Ingredient created successfully');
      setIsIngredientDialogOpen(false);
    },
    onError: (error) => {
      toast.error(getIngredientErrorMessage(error, 'Unable to create ingredient'));
    },
  });

  const updateIngredientMutation = useMutation(updateIngredient, {
    onSuccess: async () => {
      await invalidateIngredients();
      toast.success('Ingredient updated successfully');
      setIsIngredientDialogOpen(false);
    },
    onError: (error) => {
      toast.error(getIngredientErrorMessage(error, 'Unable to update ingredient'));
    },
  });

  const deleteIngredientMutation = useMutation(deleteIngredient, {
    onSuccess: async () => {
      await invalidateIngredients();
      toast.success('Ingredient deleted successfully');
      setIngredientToDelete(null);
    },
    onError: (error) => {
      toast.error(getIngredientErrorMessage(error, 'Unable to delete ingredient'));
    },
  });

  const handleAddNewIngredientClick = () => {
    setEditingIngredient(null);
    setIsIngredientDialogOpen(true);
  };

  const handleEditIngredientClick = (ingredient) => {
    setEditingIngredient(ingredient);
    setIsIngredientDialogOpen(true);
  };

  const handleCloseIngredientDialog = () => {
    if (createIngredientMutation.isLoading || updateIngredientMutation.isLoading) {
      return;
    }

    setEditingIngredient(null);
    setIsIngredientDialogOpen(false);
  };

  const handleIngredientSubmit = async (payload) => {
    if (editingIngredient?.id) {
      await updateIngredientMutation.mutateAsync({
        id: editingIngredient.id,
        payload,
      });
      return;
    }

    await createIngredientMutation.mutateAsync(payload);
  };

  const handleDeleteIngredient = async () => {
    if (!ingredientToDelete?.id || deleteIngredientMutation.isLoading) {
      return;
    }

    try {
      await deleteIngredientMutation.mutateAsync(ingredientToDelete.id);
    } catch (error) {
      // Mutation-level error handling already shows the toast.
    }
  };

  return (
    <>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent='flex-end'
        alignItems={{ xs: 'flex-start', md: 'center' }}
        gap={2}
      >
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={handleAddNewIngredientClick}
        >
          Add new ingredient
        </Button>
      </Stack>

      {isLoading ? (
        <LoaderProgressComponent />
      ) : isError ? (
        <Paper sx={{ p: 4, borderRadius: '16px' }}>
          <Typography variant='s16_w500'>
            Unable to load ingredients right now.
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            border: '1px solid #E5E7EB',
            borderRadius: '16px',
            boxShadow: 'none',
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>English</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Norwegian</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '12rem' }} align='right'>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderedIngredients.length > 0 ? (
                orderedIngredients.map((ingredient) => {
                  return (
                    <TableRow key={ingredient.id} hover>
                      <TableCell>{ingredient.nameEn}</TableCell>
                      <TableCell>{ingredient.nameNo}</TableCell>
                      <TableCell align='right'>
                        <Tooltip title='Edit'>
                          <IconButton
                            onClick={() => handleEditIngredientClick(ingredient)}
                          >
                            <EditOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete'>
                          <IconButton
                            color='error'
                            onClick={() => setIngredientToDelete(ingredient)}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Box py={8} textAlign='center'>
                      <Typography variant='s16_w500' color='#6B7280'>
                        No ingredients yet. Click "Add new ingredient" to create one.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <IngredientManagementDialog
        open={isIngredientDialogOpen}
        onClose={handleCloseIngredientDialog}
        onSubmit={handleIngredientSubmit}
        ingredient={editingIngredient}
        isSubmitting={
          createIngredientMutation.isLoading || updateIngredientMutation.isLoading
        }
      />

      <DeleteIngredientDialog
        open={!!ingredientToDelete}
        onClose={() => {
          if (!deleteIngredientMutation.isLoading) {
            setIngredientToDelete(null);
          }
        }}
        onConfirm={handleDeleteIngredient}
        ingredient={ingredientToDelete}
        isDeleting={deleteIngredientMutation.isLoading}
      />
    </>
  );
};

export default IngredientManagementScreen;
